/**
 * WordPress dependencies
 */
import { Button } from '@safe-wordpress/components';
import { useDispatch } from '@safe-wordpress/data';
import { _x, sprintf } from '@safe-wordpress/i18n';

/**
 * External dependencies
 */
import { uniqBy } from 'lodash';
import { store as NAB_DATA } from '@nab/data';
import type { Opportunity } from '@nab/types';

/**
 * Internal dependencies
 */
import './style.scss';
import { CreateExperimentFromOpportunityButton } from '../create-experiment-from-opportunity-button';

export type OpportunityCardProps = {
	readonly mode: 'create-test' | 'creating-test' | 'get-suggestions';
	readonly opportunity: Opportunity;
	readonly isDisabled: boolean;
};

export function OpportunityCard( {
	mode,
	opportunity,
	isDisabled,
}: OpportunityCardProps ): JSX.Element {
	const description = getOpportunityDescription( opportunity );
	const reasons = getOpportunityReasons( opportunity );

	const { generateExperimentSuggestions } = useDispatch( NAB_DATA );

	return (
		<article className="nab-opportunity-card nab-overview-card">
			<div className="nab-opportunity-card__content">
				<div className="nab-opportunity-card__topline">
					<h3 className="nab-opportunity-card__card-title">
						{ opportunity.target.title }
					</h3>
					<span className="nab-opportunity-card__score">
						{ sprintf(
							/* translators: %d: Numeric score. */
							_x( 'Score %d', 'text', 'nelio-ab-testing' ),
							opportunity.score
						) }
					</span>
				</div>

				<div className="nab-opportunity-card__reasons">
					{ reasons.map( ( reason ) => (
						<span
							key={ reason.label }
							className={ `nab-opportunity-card__tag nab-opportunity-card__tag--${ reason.color }` }
						>
							{ reason.label }
						</span>
					) ) }
				</div>

				<p className="nab-opportunity-card__text">{ description }</p>

				<div className="nab-opportunity-card__actions">
					{ 'get-suggestions' === mode ? (
						<Button
							variant="primary"
							onClick={ () =>
								generateExperimentSuggestions( opportunity )
							}
						>
							{ _x(
								'Generate test ideas',
								'command',
								'nelio-ab-testing'
							) }
						</Button>
					) : (
						<CreateExperimentFromOpportunityButton
							isBusy={ 'creating-test' === mode }
							isDisabled={ isDisabled }
							opportunity={ opportunity }
							variant="primary"
						/>
					) }
				</div>
			</div>
		</article>
	);
}

// =======
// HELPERS
// =======

type OpportunityReason = {
	readonly label: string;
	readonly color: 'green' | 'orange' | 'purple' | 'blue' | 'pink';
};

function getOpportunityDescription( opportunity: Opportunity ): string {
	switch ( opportunity.type ) {
		case 'multiple-reasons':
			return _x(
				'This page stands out as a strong candidate for your next test.',
				'text',
				'nelio-ab-testing'
			);

		case 'key-page':
			return opportunity.meta.reason;

		case 'high-traffic':
			return _x(
				'This page gets meaningful traffic and may be worth optimizing.',
				'text',
				'nelio-ab-testing'
			);

		case 'recently-finished-experiment':
			return _x(
				'Use what you learned from the recent test to inform the next iteration.',
				'user',
				'nelio-ab-testing'
			);

		case 'recently-published':
			return _x(
				'This page was recently published and has not been optimized yet.',
				'text',
				'nelio-ab-testing'
			);

		case 'stale-experiment':
			return _x(
				'User behavior changes over time, so it’s worth testing this page again.',
				'text',
				'nelio-ab-testing'
			);
	}
}

function getOpportunityReasons(
	opportunity: Opportunity
): ReadonlyArray< OpportunityReason > {
	if ( opportunity.type === 'multiple-reasons' ) {
		return uniqBy(
			opportunity.meta.opportunities.flatMap( getOpportunityReasons ),
			'label'
		);
	}

	switch ( opportunity.type ) {
		case 'key-page':
			return [
				{
					label: _x( 'Key page', 'text', 'nelio-ab-testing' ),
					color: 'pink',
				},
			];

		case 'high-traffic':
			return [
				{
					label: _x( 'High traffic', 'text', 'nelio-ab-testing' ),
					color: 'green',
				},
			];

		case 'recently-finished-experiment':
			return [
				{
					label: _x( 'Recently tested', 'text', 'nelio-ab-testing' ),
					color: 'blue',
				},
			];

		case 'recently-published':
			return [
				{
					label: _x(
						'Recently published',
						'text',
						'nelio-ab-testing'
					),
					color: 'purple',
				},
			];

		case 'stale-experiment':
			return [
				{
					label: _x( 'Stale test', 'text', 'nelio-ab-testing' ),
					color: 'orange',
				},
			];
	}
}
